import React, { useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ReferenceDot,
} from 'recharts';

const DIMENSION_COLORS = {
  overall: '#1e293b',
  skill_coverage: '#3b82f6',
  clarity: '#5AAFA7',
  impact: '#f59e0b',
  structure: '#8b5cf6',
  relevance: '#10b981',
  narrative: '#ec4899',
};

const DIMENSION_LABELS = {
  overall: 'Overall',
  skill_coverage: 'Skill Coverage',
  clarity: 'Clarity',
  impact: 'Impact',
  structure: 'Structure',
  relevance: 'Relevance',
  narrative: 'Narrative',
};

const EVENT_ICONS = {
  upload: '⬆️',
  ai_polish: '✨',
  job_tailored: '🎯',
  diagnosis: '🩺',
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white px-3 py-2 rounded-lg border border-gray-200 shadow-sm text-xs">
      <p className="font-medium text-gray-700 mb-1">
        {new Date(label).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
      </p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-gray-500">{DIMENSION_LABELS[p.dataKey] || p.dataKey}:</span>
          <span className="font-semibold text-gray-800">{p.value ?? '—'}</span>
        </div>
      ))}
    </div>
  );
};

const ResumeEvolution = ({ scoresOverTime = [], resumeEvents = [], currentRadar = [], firstRadar = [] }) => {
  const [activeDimensions, setActiveDimensions] = useState(['overall']);

  const safeScores = Array.isArray(scoresOverTime) ? scoresOverTime : [];
  const safeEvents = Array.isArray(resumeEvents) ? resumeEvents : [];
  const safeCurrentRadar = Array.isArray(currentRadar) ? currentRadar : [];
  const safeFirstRadar = Array.isArray(firstRadar) ? firstRadar : [];

  const toggleDimension = (dim) => {
    setActiveDimensions(prev => {
      if (prev.includes(dim)) {
        return prev.length > 1 ? prev.filter(d => d !== dim) : prev;
      }
      return [...prev, dim];
    });
  };

  const fmtDate = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  const first = safeScores[0]?.overall ?? 0;
  const latest = safeScores[safeScores.length - 1]?.overall ?? 0;
  const improvement = latest - first;

  return (
    <div className="bg-white shadow-sm rounded-xl overflow-hidden p-5 border-t-4 border-primary-500 h-full flex flex-col">
      <div className="flex items-start justify-between mb-1">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Resume Evolution</h3>
          <p className="text-xs text-gray-500">Score progression over time</p>
        </div>
        {safeScores.length > 0 && (
          <div className={`flex items-center gap-1.5 rounded-lg px-2 py-1 ${improvement >= 0 ? 'bg-green-50' : 'bg-orange-50'}`}>
            <span className={`text-xs font-semibold ${improvement >= 0 ? 'text-green-700' : 'text-orange-600'}`}>
              {improvement >= 0 ? '+' : ''}{improvement} pts
            </span>
            <span className={`text-[10px] ${improvement >= 0 ? 'text-green-600' : 'text-orange-500'}`}>since start</span>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5 my-3">
        {Object.entries(DIMENSION_LABELS).map(([key, label]) => {
          const isActive = activeDimensions.includes(key);
          return (
            <button
              key={key}
              onClick={() => toggleDimension(key)}
              className={`text-[10px] px-2 py-0.5 rounded-full border transition-all ${
                isActive
                  ? 'border-transparent text-white font-medium'
                  : 'border-gray-200 text-gray-500 hover:border-gray-300'
              }`}
              style={isActive ? { backgroundColor: DIMENSION_COLORS[key] } : {}}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/*
        Body: a 5-col grid (line chart on the left taking 3, radar on the
        right taking 2). The grid itself takes flex-1 so the body fills the
        remaining vertical space inside the panel; each chart wrapper uses
        `flex-1 min-h-[200px]` so the SVG charts expand vertically with the
        panel instead of leaving a dead zone at the bottom.
      */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 flex-1 min-h-0">
        <div className={`${safeCurrentRadar.length > 0 ? 'lg:col-span-3' : 'lg:col-span-5'} min-w-0 flex flex-col`}>
          {safeScores.length > 0 ? (
            <div className="flex-1 min-h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={safeScores} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                  <Tooltip content={<CustomTooltip />} />
                  {activeDimensions.map(dim => (
                    <Line
                      key={dim}
                      type="monotone"
                      dataKey={dim}
                      stroke={DIMENSION_COLORS[dim]}
                      strokeWidth={dim === 'overall' ? 2.5 : 1.5}
                      dot={{ r: 3, fill: DIMENSION_COLORS[dim] }}
                      activeDot={{ r: 5 }}
                      connectNulls
                    />
                  ))}
                  {safeEvents.map((evt, i) => {
                    const matchingScore = safeScores.find(s => Math.abs(new Date(s.date) - new Date(evt.date)) < 86400000 * 5);
                    if (!matchingScore) return null;
                    return (
                      <ReferenceDot
                        key={i}
                        x={matchingScore.date}
                        y={matchingScore.overall}
                        r={0}
                        label={{ value: EVENT_ICONS[evt.type] || '📌', position: 'top', fontSize: 14 }}
                      />
                    );
                  })}
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center py-10 text-center text-xs text-gray-400">
              Run a resume diagnosis to start charting your progress.
            </div>
          )}

          {safeEvents.length > 0 && (
            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-3 pt-3 border-t border-gray-100 text-[10px] text-gray-500">
              {safeEvents.slice(-6).map((evt, i) => (
                <span key={i} className="flex items-center gap-1">
                  {EVENT_ICONS[evt.type] || '📌'} {fmtDate(evt.date)} — {evt.label}
                </span>
              ))}
            </div>
          )}
        </div>

        {safeCurrentRadar.length > 0 && (
          <div className="lg:col-span-2 lg:pl-4 lg:border-l lg:border-gray-100 flex flex-col">
            <p className="text-xs font-semibold text-gray-700 mb-1">Current vs. First Diagnosis</p>
            <div className="flex-1 min-h-[180px] flex items-center justify-center">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={safeCurrentRadar}>
                  <PolarGrid stroke="#e2e8f0" />
                  <PolarAngleAxis dataKey="dimension" tick={{ fontSize: 9, fill: '#64748b' }} />
                  <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                  <Radar
                    name="Current"
                    dataKey="score"
                    data={safeCurrentRadar}
                    stroke="#5AAFA7"
                    fill="#5AAFA7"
                    fillOpacity={0.25}
                    strokeWidth={2}
                  />
                  {safeFirstRadar.length > 0 && (
                    <Radar
                      name="First"
                      dataKey="score"
                      data={safeFirstRadar}
                      stroke="#f59e0b"
                      fill="#f59e0b"
                      fillOpacity={0.1}
                      strokeWidth={1.5}
                      strokeDasharray="4 3"
                    />
                  )}
                </RadarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-4 text-[10px] mt-1">
              <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-primary-500 inline-block" /> Current</span>
              {safeFirstRadar.length > 0 && (
                <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-amber-500 inline-block border-dashed" /> First</span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResumeEvolution;
